import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageSquare } from "lucide-react";
import Footer from "../components/site/Footer";

export default function SmsTerms() {
  useEffect(() => {
    document.title = "Campaign Terms of Service · Elevare Solutions";
    const desc = document.querySelector('meta[name="description"]');
    const prev = desc?.getAttribute("content");
    if (desc) desc.setAttribute("content",
      "Elevare Solutions SMS Messaging Terms & Conditions — consent, message frequency, message and data rates, and how to opt out (reply STOP) or get help (reply HELP).");
    window.scrollTo(0, 0);
    return () => { if (desc && prev) desc.setAttribute("content", prev); };
  }, []);

  const updated = "June 11, 2026";

  return (
    <div className="min-h-screen bg-[#ded9cb] text-[#1e3d3a]">
      <header className="border-b border-black/10 bg-[#eae7de]/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="sms-terms-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">
            <ArrowLeft className="h-3.5 w-3.5" /> Elevare Solutions
          </Link>
          <span className="text-[10px] uppercase tracking-[0.22em] text-black/40">Updated {updated}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="sms-terms-page">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Legal · Messaging Program</p>
        <h1 className="mt-4 font-head text-4xl lg:text-5xl font-medium tracking-tight">Campaign Terms of Service</h1>

        <div className="mt-6 rounded-2xl border border-[#a3823f]/30 bg-[#eae7de]/60 px-5 py-4 flex items-start gap-3">
          <MessageSquare className="h-5 w-5 text-[#a3823f] shrink-0 mt-0.5" />
          <p className="text-sm text-black/70 leading-relaxed">
            <strong>Quick summary:</strong> Elevare Solutions sends business outreach and account text
            messages. Message frequency varies. <strong>Message and data rates may apply.</strong>{" "}
            Reply <strong>STOP</strong> at any time to unsubscribe, or <strong>HELP</strong> for help.
            We never share your mobile number or opt-in data with third parties for their own marketing.
          </p>
        </div>

        <p className="mt-8 text-sm text-black/60 leading-relaxed">
          These SMS Messaging Terms &amp; Conditions (&ldquo;SMS Terms&rdquo;) govern the text-message
          (SMS/MMS) program (the &ldquo;Program&rdquo;) operated by Elevare Solutions LLC
          (&ldquo;Elevare,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;). By providing
          your mobile phone number to us, or by replying to a message we send you, you agree to these
          SMS Terms, our <Link className="text-[#a3823f] hover:underline" to="/terms">Terms of Service</Link>{" "}
          and our <Link className="text-[#a3823f] hover:underline" to="/privacy-policy">Privacy Policy</Link>.
          If you do not agree, do not opt in and reply STOP to any message you receive.
        </p>

        <Section title="1. Program Description">
          The Program sends business-to-business outreach, appointment and booking reminders, service
          and account notifications, replies to your inquiries, and related informational and
          promotional messages regarding cost-reduction, BBB reputation, and Elevare&rsquo;s services.
          Message content and frequency vary depending on your interaction with us.
        </Section>

        <Section title="2. Consent to Receive Messages">
          By giving us your mobile number (for example on a form, during a call, in an email signature
          you publish for business contact, or by texting us first) you represent that you are the
          subscriber or customary user of that number and that you are authorized to consent to receive
          texts at it. Where required by law, we obtain your prior express consent before sending
          promotional texts. <strong>Consent is not a condition of purchasing any goods or
          services.</strong> You may withdraw consent at any time as described below.
        </Section>

        <Section title="3. Message Frequency">
          Message frequency is recurring and varies based on your interaction with Elevare (for example,
          conversation replies, reminders, and follow-ups). You may receive one or more messages per
          conversation. We do not send a fixed number of messages per period.
        </Section>

        <Section title="4. Message &amp; Data Rates">
          <strong>Message and data rates may apply</strong> to each message sent to or received from you,
          according to the plan and terms of your wireless carrier. Elevare does not charge for the
          messages themselves, but your carrier may. Contact your carrier for details about your
          messaging plan.
        </Section>

        <Section title="5. How to Opt Out — Reply STOP">
          You can cancel the Program at any time by replying <strong>STOP</strong> (or UNSUBSCRIBE,
          CANCEL, END, or QUIT) to any message you receive from us. After you send STOP, we will send
          you one final confirmation message and then stop sending Program messages to that number. You
          understand that we may take up to ten (10) business days to process a request made through any
          channel other than replying STOP. If you opt out you may no longer receive replies to
          questions you text us; text START to re-subscribe.
        </Section>

        <Section title="6. How to Get Help — Reply HELP">
          For help at any time, reply <strong>HELP</strong> to any message, email{" "}
          <a className="text-[#a3823f] hover:underline" href="mailto:john@elevaresolutions.me">john@elevaresolutions.me</a>,
          or call the numbers listed in the Contact section below. Replying HELP will return
          Elevare&rsquo;s contact information and support instructions.
        </Section>

        <Section title="7. Supported Carriers">
          Carriers (including but not limited to AT&amp;T, T-Mobile, Verizon Wireless, Sprint, and their
          affiliates and MVNOs) are not liable for delayed or undelivered messages. Message delivery is
          subject to effective transmission by your wireless carrier and is not guaranteed. Carriers are
          not parties to these SMS Terms.
        </Section>

        <Section title="8. Your Privacy">
          We respect your privacy. Mobile information &mdash; including your phone number and consent
          status &mdash; is used only to operate the Program and is handled in accordance with our{" "}
          <Link className="text-[#a3823f] hover:underline" to="/privacy-policy">Privacy Policy</Link>.{" "}
          <strong>We do not sell or share your mobile opt-in information or phone number with third
          parties or affiliates for their own marketing or promotional purposes.</strong> We may share
          it with service providers (such as our messaging platform, Twilio) solely to deliver the
          Program on our behalf.
        </Section>

        <Section title="9. No Unlawful or Prohibited Content">
          The Program is used for lawful business communication only. We do not send content prohibited
          by carrier or industry rules (the &ldquo;SHAFT&rdquo; categories &mdash; sex, hate, alcohol,
          firearms, tobacco &mdash; or other prohibited content). If you believe you received a message
          in error, reply STOP and contact us.
        </Section>

        <Section title="10. Changes to the Program or These SMS Terms">
          We may change or terminate the Program, or update these SMS Terms, at any time by posting a
          revised version on this page. Your continued participation after the &ldquo;Updated&rdquo; date
          above constitutes acceptance of the change. If we materially change how we use your mobile
          information we will provide notice as required by law.
        </Section>

        <Section title="11. Disclaimer &amp; Limitation of Liability">
          THE PROGRAM IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo; TO THE FULLEST
          EXTENT PERMITTED BY LAW, ELEVARE DISCLAIMS ALL WARRANTIES REGARDING THE PROGRAM AND IS NOT
          LIABLE FOR ANY DELAYED OR UNDELIVERED MESSAGES, FOR ANY CARRIER CHARGES, OR FOR ANY INDIRECT,
          INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR PARTICIPATION IN THE PROGRAM.
        </Section>

        <Section title="12. Contact">
          Elevare Solutions LLC<br />
          Kalib Whaley &mdash; Founder &amp; CEO &mdash; <a className="text-[#a3823f] hover:underline" href="mailto:kalib@elevaresolutions.me">kalib@elevaresolutions.me</a> &mdash; (260) 200-0907<br />
          John Caron &mdash; Co-Founder &amp; COO &mdash; <a className="text-[#a3823f] hover:underline" href="mailto:john@elevaresolutions.me">john@elevaresolutions.me</a> &mdash; (931) 802-3183
        </Section>

        <p className="mt-16 text-[10px] uppercase tracking-[0.22em] text-black/40">
          Last updated {updated}. See also our{" "}
          <Link className="text-[#a3823f] hover:underline" to="/terms">Terms of Service</Link> and{" "}
          <Link className="text-[#a3823f] hover:underline" to="/privacy-policy">Privacy Policy</Link>.
        </p>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="font-head text-xl text-[#1e3d3a]">{title}</h2>
      <p className="mt-3 text-sm text-black/70 leading-relaxed">{children}</p>
    </section>
  );
}
